import type { SqliteRemoteDatabase } from 'drizzle-orm/sqlite-proxy';
import type { SQLocal } from 'sqlocal';

import * as schema from './schema';

export async function seed(tx: SQLocal['transaction'], db: SqliteRemoteDatabase<typeof schema>) {
	await tx(async (tx) => {
		await tx.query(db.insert(schema.instance).values({}).onConflictDoNothing());

		await tx.query(db.insert(schema.profiles).values(defaultProfile).onConflictDoNothing());

		await tx.query(
			db
				.insert(schema.providers)
				.values(freeProxy)
				.onConflictDoUpdate({
					target: schema.providers.id,
					set: {
						...freeProxy,
						isEnabled: undefined
					}
				})
		);

		await tx.query(
			db
				.insert(schema.prompts)
				.values(defaultPrompt)
				.onConflictDoUpdate({
					target: schema.prompts.id,
					set: {
						...defaultPrompt,
						isEnabled: undefined,
						// never overwrite default prompt
						isDefault: undefined
					}
				})
		);

		await tx.query(
			db
				.insert(schema.prompts)
				.values(softwareEngineeringPrompt)
				.onConflictDoUpdate({
					target: schema.prompts.id,
					set: {
						...softwareEngineeringPrompt,
						isEnabled: undefined
					}
				})
		);
		await tx.query(
			db
				.insert(schema.tools)
				.values(webBrowsingTool)
				.onConflictDoUpdate({
					target: schema.tools.id,
					set: {
						...webBrowsingTool,
						isEnabled: undefined
					}
				})
		);
		await tx.query(
			db
				.insert(schema.tools)
				.values(searchEngineTool)
				.onConflictDoUpdate({
					target: schema.tools.id,
					set: {
						...searchEngineTool,
						isEnabled: undefined
					}
				})
		);
		await tx.query(
			db
				.insert(schema.tools)
				.values(calculatorTool)
				.onConflictDoUpdate({
					target: schema.tools.id,
					set: {
						...calculatorTool,
						isEnabled: undefined
					}
				})
		);
		await tx.query(
			db
				.insert(schema.tools)
				.values(imageTool)
				.onConflictDoUpdate({
					target: schema.tools.id,
					set: {
						...imageTool,
						isEnabled: undefined
					}
				})
		);
		await tx.query(
			db
				.insert(schema.inferenceSettings)
				.values(defaultChatInferenceSettings)
				.onConflictDoUpdate({
					target: schema.inferenceSettings.id,
					set: {
						...defaultChatInferenceSettings
					}
				})
		);
		await tx.query(
			db
				.insert(schema.inferenceSettings)
				.values(defaultToolInferenceSettings)
				.onConflictDoUpdate({
					target: schema.inferenceSettings.id,
					set: {
						...defaultToolInferenceSettings
					}
				})
		);
		await tx.query(
			db
				.insert(schema.automations)
				.values(createChatTitleAutomation)
				.onConflictDoUpdate({
					target: schema.automations.id,
					set: {
						...createChatTitleAutomation,
						isEnabled: undefined,
						inferenceSettingsId: undefined
					}
				})
		);
	});
}

const defaultProfile: typeof schema.profiles.$inferInsert = {
	displayName: '',
	avatarStyle: 'thumbs'
};

const freeProxy: typeof schema.providers.$inferInsert = {
	id: 'quill',
	title: 'Quill (free, rate-limited)',
	apiEndpointUrl: '/api/proxy',
	// TODO: API flavor is incorrect.
	// However, this provider is currently not available anways.
	apiFlavor: 'open-router',
	apiKey: '',
	isDeleted: true,
	isPreset: true
};

const defaultPrompt: typeof schema.prompts.$inferInsert = {
	id: 'default',
	title: 'Default',
	content: [
		'You are a helpful assistant.',
		'The responses are concise and to the point.',
		'The tone is casual and professional.',
		'{{#if user.display_name}}The username is {{user.display_name}}.{{/if}}',
		'The current date is {{date}}.'
	].join('\n'),
	isDefault: true,
	isPreset: true
};

const softwareEngineeringPrompt: typeof schema.prompts.$inferInsert = {
	id: 'software-engineering',
	title: 'Software Engineering',
	content: [
		'You are a senior expert software engineer.',
		'The responses are concise and to the point.',
		'The tone is casual but professional.',
		'Before jumping to conclusions or code, you first think through the problem step by step.',
		'You apply software engineering best practices, and always produce clean code.',
		'{{#if user.display_name}}The username is {{user.display_name}}.{{/if}}',
		'The current date is {{date}}.'
	].join('\n'),
	isPreset: true
};

const webBrowsingTool: typeof schema.tools.$inferInsert = {
	id: 'web-browsing',
	title: 'Web Browsing',
	identifier: 'browse_web',
	description:
		'Enables LLMs to browse the web. Can not use a search engine, and must be provided a specific URL to visit.',
	isDeleted: false
};

const searchEngineTool: typeof schema.tools.$inferInsert = {
	id: 'search-engine',
	title: 'Search Engine',
	identifier: 'search_web',
	description: 'Enables LLMs to use web search engines.',
	isEnabled: false,
	isDeleted: true
};

const calculatorTool: typeof schema.tools.$inferInsert = {
	id: 'calculator',
	title: 'Calculator',
	identifier: 'calculate',
	description: 'Enables LLMs to perform simple mathematical calculations.',
	isEnabled: false,
	isDeleted: true
};

const imageTool: typeof schema.tools.$inferInsert = {
	id: 'gen-image',
	title: 'Generate images',
	identifier: 'generate_image',
	description: 'Enables LLMs to generate images.',
	isEnabled: false,
	isDeleted: false
};

const defaultChatInferenceSettings: typeof schema.inferenceSettings.$inferInsert = {
	id: 'default',
	providerId: 'quill',
	modelId: 'openai/gpt-4o-mini',
	isPreset: true,
	isDefault: true
};

const defaultToolInferenceSettings: typeof schema.inferenceSettings.$inferInsert = {
	id: 'default-tool',
	isPreset: true,
	providerId: 'quill',
	modelId: 'meta-llama/llama-3.1-8b-instruct'
};

const createChatTitleAutomation: typeof schema.automations.$inferInsert = {
	id: 'auto-chat-titles',
	title: 'Generate chat titles',
	description: 'Automatically generates titles for chat conversations.',
	inferenceSettingsId: 'default-tool'
};
